import React, { useState, useContext, useEffect, useCallback  } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase_utils/firebase';
import { AuthContext } from '../../contexts/AuthContext';
import ErrorBoundary from '../common/ErrorBoundary';
import { motion } from 'framer-motion';
import { 
  LogOut,
  User,
  Menu,
  X,
  Wand2,
  Book,
  Feather,
  Images,
  Home,
  Bird,
  CreditCard,
  BookOpen
} from 'lucide-react';
import CreditsDisplay from '../common/CreditsDisplay';

const LoginModal = React.lazy(() => import('../LoginModal/LoginModal'));

// Animated sidebar item for child-friendly interface
const SidebarItem = React.memo(({ to, icon: Icon, text, onClick, isCollapsed }) => (
  <motion.li 
    whileHover={{ x: 5, scale: 1.02 }} 
    whileTap={{ scale: 0.95 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
  >
    {isCollapsed ? (
      <div className="relative group">
        <Link 
          to={to} 
          onClick={(e) => onClick(e, to)}
          className="flex items-center gap-3 px-3 py-2 text-[#5E60CE] rounded-xl bg-white/50 hover:bg-gradient-to-r hover:from-white hover:to-[#E5FB5F]/20 hover:shadow-md transition-all cursor-pointer justify-center border-2 border-transparent hover:border-[#E5FB5F]/50"
        >
          {typeof Icon === 'function' ? <Icon className="w-5 h-5 flex-shrink-0" /> : <Icon className="w-5 h-5 flex-shrink-0" />}
        </Link>
        <div className="absolute left-full ml-3 px-3 py-1.5 bg-white text-[#5E60CE] text-sm font-bold rounded-xl shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 top-1/2 -translate-y-1/2 border border-[#E5FB5F]/50 z-50">
          {text}
          <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rotate-45 border-l border-b border-[#E5FB5F]/50"></div>
        </div>
      </div>
    ) : (
      <Link 
        to={to} 
        onClick={(e) => onClick(e, to)}
        className="flex items-center gap-3 px-4 py-3 text-[#5E60CE] rounded-xl bg-white/50 hover:bg-gradient-to-r hover:from-white hover:to-[#E5FB5F]/20 hover:shadow-md transition-all cursor-pointer group border-2 border-transparent hover:border-[#E5FB5F]/50"
      >
        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-white group-hover:bg-gradient-to-br group-hover:from-[#E5FB5F]/50 group-hover:to-white group-hover:shadow-sm transition-all">
          {typeof Icon === 'function' ? <Icon className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <span className="text-sm font-bold font-display">{text}</span>
      </Link>
    )}
  </motion.li>
));

// Redesigned footer for child-friendly theme
const Footer = ({ isCollapsed, user, isAuthLoading, handleAuthAction, handleProButtonClick }) => {
  if (isCollapsed) {
    return (
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#E6EFFD] bg-gradient-to-b from-transparent to-[#E5FB5F]/10">
        {user && (
          <div className="mb-3">
            <div className="relative group">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="w-full flex flex-col items-center justify-center rounded-xl p-1.5 hover:bg-white/80 hover:shadow-md transition-all cursor-help"
              >
                <span className="text-xs font-bold text-[#1A202C] mb-1 bg-gradient-to-r from-[#E5FB5F] to-white px-2.5 py-0.5 rounded-full shadow-sm border border-[#D1E2FB]">
                  <CreditsDisplay showCount={true} hideIcon={true} />
                </span>
                <CreditCard className="w-4 h-4 text-[#5E60CE]" />
              </motion.div>
              <div className="absolute left-full ml-3 px-3 py-1.5 bg-white text-[#5E60CE] text-sm font-bold rounded-xl shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-[#E5FB5F]/50">
                Your Credits
                <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rotate-45 border-l border-b border-[#E5FB5F]/50"></div>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div className="relative group">
            <motion.button 
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleAuthAction()}
              disabled={isAuthLoading}
              className="w-full flex justify-center px-2 py-2 text-sm font-medium text-[#FF6B6B] bg-white rounded-xl shadow-sm hover:shadow-md border border-transparent hover:border-[#FF6B6B]/30 transition-all cursor-pointer"
            >
              <LogOut className="w-5 h-5" />
            </motion.button>
            <div className="absolute left-full ml-3 px-3 py-1.5 bg-white text-[#FF6B6B] text-sm font-bold rounded-xl shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 top-1/2 -translate-y-1/2 border border-[#FF6B6B]/20 z-50">
              {user ? 'Sign Out' : 'Sign Up'}
              <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rotate-45 border-l border-b border-[#FF6B6B]/20"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E6EFFD] bg-white">
      {user && (
        <>
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-3 px-2 p-2 rounded-xl hover:bg-gray-50 transition-colors"
          >
            {user.photoURL ? (
              <img 
                src={user.photoURL} 
                alt="" 
                className="w-10 h-10 rounded-full border-2 border-[#E5FB5F] shadow-sm"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#F0F7FF] flex items-center justify-center border-2 border-[#E6EFFD]">
                <User className="w-5 h-5 text-[#5E60CE]" />
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-sm font-bold text-[#5E60CE] font-display truncate max-w-[120px]">
                {user.displayName || 'Story Creator'}
              </span>
              <CreditsDisplay className="mt-1" />
            </div>
          </motion.div>
        </>
      )}

      <div className="space-y-3">
        <motion.button 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleAuthAction()}
          disabled={isAuthLoading}
          className="w-full px-4 py-3 text-sm font-bold text-[#FF6B6B] bg-white border-2 border-[#FF6B6B]/10 rounded-xl hover:bg-[#FF6B6B]/5 hover:border-[#FF6B6B]/30 transition-all cursor-pointer font-display shadow-sm"
        >
          {isAuthLoading ? 'Loading...' : user ? 'Sign Out' : 'Sign Up'}
        </motion.button>
      </div>
    </div>
  );
};

function Sidebar({ forceCollapsed }) {
  // If forceCollapsed is true, always use true for isCollapsed state
  const [isCollapsed, setIsCollapsed] = useState(forceCollapsed || true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginReason, setLoginReason] = useState('');

  // Update isCollapsed when forceCollapsed changes
  useEffect(() => {
    if (forceCollapsed) {
      setIsCollapsed(true);
    }
  }, [forceCollapsed]);

  const protectedPaths = [
    '/app/agents'
  ];

  const handleAuthAction = useCallback((reason = '') => {
    if (user) {
      setIsAuthLoading(true);
      signOut(auth).then(() => {
        setIsAuthLoading(false);
      }).catch((error) => {
        // console.error('Sign out error:', error);
        setError(error.message);
        setIsAuthLoading(false);
      });
    } else {
      setLoginReason(reason);
      setShowLoginModal(true);
    }
  }, [user]);

  const handleLoginSuccess = useCallback(() => {
    setShowLoginModal(false);
    setLoginReason('');
    if (loginReason === 'pro') {
      navigate('/contact');
    } else {
      navigate('/app');
    }
  }, [loginReason, navigate]);

  const handleProButtonClick = useCallback(() => {
    if (user) {
      navigate('/contact');
    } else {
      handleAuthAction('pro');
    }
  }, [user, navigate, handleAuthAction]);

  const handleSidebarItemClick = useCallback((e, path, isExternal) => {
    if (isExternal) {
      e.preventDefault();
      window.location.href = path;
      return;
    }

    // Check if the path or its root is protected
    const isProtected = protectedPaths.some(protectedPath => 
      path === protectedPath || path.startsWith(`${protectedPath}/`)
    );

    if (isProtected && !user) {
      e.preventDefault();
      handleAuthAction();  // This shows the login modal
    } else {
      // Check if we're already on this route
      const currentPath = window.location.pathname;
      if (currentPath === path) {
        // If we're clicking on the current path, refresh the page
        e.preventDefault();
        window.location.href = path;
      } else {
        navigate(path);
      }
    }
  }, [user, navigate, handleAuthAction]);

  // Updated sidebarItems with Home as the first item
  const sidebarItems = [
    { 
      to: "/app", 
      icon: Home,
      text: "Home" 
    },
    { 
      to: "/app/tools/quick-story", 
      icon: Wand2,
      text: "Quick Story" 
    },
    { 
      to: "/app/tools/story", 
      icon: Feather,
      text: "Story Book" 
    },
    {
      to: "/app/tools/story-narrator",
      icon: Bird,
      text: "Fable Whisperer"
    },
    {
      to: "/kids-learning/index.html",
      icon: BookOpen,
      text: "Kids Learning",
      isExternal: true
    }
  ];

  return (
    <ErrorBoundary componentName="Sidebar">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" />
      )}

      {/* Mobile Menu Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed top-4 left-4 p-2 rounded-full bg-white shadow-md sm:hidden z-50"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? (
          <X className="w-5 h-5 text-[#5E60CE]" />
        ) : (
          <Menu className="w-5 h-5 text-[#5E60CE]" />
        )}
      </motion.button>

      <aside 
        className="fixed top-0 left-0 h-full bg-gradient-to-b from-[#FAFBFF] via-[#F5F8FF] to-[#E5FB5F]/10 border-r border-[#E6EFFD] 
          transition-all duration-300 z-50 sidebar w-16"
      >
        <div className="h-20 flex items-center justify-center px-4 border-b border-[#E6EFFD] bg-white/50 backdrop-blur-sm">
          <div className="relative group">
            <Link 
              to="/" 
              className="flex items-center justify-center hover:text-[#4F5ABF] transition-colors"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden group bg-gradient-to-br from-[#E5FB5F]/20 to-white p-0.5">
                <img 
                  src="/images/logo/kuchipuchi_logo1.png" 
                  alt="Kuchipuchi Logo" 
                  className="w-10 h-10 object-contain transition-all duration-300 transform group-hover:scale-110"
                />
              </div>
            </Link>
            <div className="absolute left-full ml-2 px-2 py-1 bg-gradient-to-r from-[#5E60CE] to-[#4F5ABF] text-white text-sm rounded-md whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 top-1/2 -translate-y-1/2">
              KuchiPuchi
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-1">
          <ul className="space-y-1">
            {sidebarItems.map((item) => (
              <SidebarItem
                key={item.to}
                {...item}
                isCollapsed={true}
                onClick={(e, path) => {
                  handleSidebarItemClick(e, path, item.isExternal);
                  setIsMobileMenuOpen(false);
                }}
              />
            ))}
          </ul>
        </nav>

        <Footer 
          isCollapsed={true}
          user={user}
          isAuthLoading={isAuthLoading}
          handleAuthAction={handleAuthAction}
          handleProButtonClick={handleProButtonClick}
        />
      </aside>

      <React.Suspense fallback={null}>
        <LoginModal 
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          loginReason={loginReason}
          onLoginSuccess={handleLoginSuccess}
        />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default React.memo(Sidebar);
